// @flow
import omit from '@justpark/helpers/src/omit/omit';
import isEmpty from '@justpark/helpers/src/isEmpty/isEmpty';
import {
  createAsyncThunk,
  createSelector,
  createSlice
} from '@reduxjs/toolkit';
import getPaymentMethods from '@justpark/api/src/requests/getPaymentMethods';
import createPaymentMethodApi from '@justpark/api/src/requests/createPaymentMethod';
import deletePaymentMethodApi from '@justpark/api/src/requests/deletePaymentMethod';
import getRequestErrorData from '@justpark/api/src/helpers/getRequestErrorData';
import { makeActionCreator, getIdxById } from '../helpers/utils';
import { notify } from '../helpers/BugsnagClient';
import applePaymentItem from '../helpers/applePaymentItem';
import googlePaymentItem from '../helpers/googlePaymentItem';

const debug = require('debug')('jp:reducers:payments');

const sliceName = 'payments';

export const TOGGLE_USER_ADD_PAYMENTMETHOD =
  'payments/TOGGLE_USER_ADD_PAYMENTMETHOD';
export const TOGGLE_ALL_PAYMENTMETHODS = 'payments/TOGGLE_ALL_PAYMENTMETHODS';
export const SELECT_PAYMENTMETHOD = 'payments/SELECT_PAYMENTMETHOD';
export const SET_PAYMENT_DETAILS_LOCK = 'payments/SET_PAYMENT_DETAILS_LOCK';
export const SET_PAYMENT_DETAILS_SUBMITTING =
  'payments/SET_PAYMENT_DETAILS_SUBMITTING';
export const CARD_TOKENIZING = 'payments/CARD_TOKENIZING';
export const ADD_PROMO_CODE = 'payments/ADD_PROMO_CODE';
export const ADD_PROMO_CODE_SUCCESS = 'payments/ADD_PROMO_CODE_SUCCESS';
export const ADD_PROMO_CODE_FAIL = 'payments/ADD_PROMO_CODE_FAIL';
export const FILTER_PAYMENT_METHODS = 'payments/FILTER_PAYMENT_METHODS';
export const HOSTED_FIELDS_ERRORS = 'payments/HOSTED_FIELDS_ERRORS';
export const CLEAR_GLOBAL_API_ERROR = 'payments/CLEAR_GLOBAL_API_ERROR';

export const SET_GOOGLE_PAY_AVAILABLE = 'payments/SET_GOOGLE_PAY_AVAILABLE';
export const SET_GOOGLE_PAY_HAS_INITIALISED =
  'payments/SET_GOOGLE_PAY_HAS_INITIALISED';
export const SET_APPLE_PAY_AVAILABLE = 'payments/SET_APPLE_PAY_AVAILABLE';
export const SET_APPLE_PAY_HAS_INITIALISED =
  'payments/SET_APPLE_PAY_HAS_INITIALISED';
export const SET_INITIAL_STATE = 'payments/SET_INITIAL_STATE';

type Action =
  | {| type: typeof TOGGLE_ALL_PAYMENTMETHODS |}
  | {| type: typeof SET_PAYMENT_DETAILS_LOCK, lock: boolean |}
  | {| type: typeof CARD_TOKENIZING |}
  | {| type: typeof SET_PAYMENT_DETAILS_SUBMITTING, submitting: boolean |}
  | {| type: typeof TOGGLE_USER_ADD_PAYMENTMETHOD, bool: boolean |}
  | {| type: typeof ADD_PROMO_CODE |}
  | {| type: typeof ADD_PROMO_CODE_SUCCESS |}
  | {| type: typeof ADD_PROMO_CODE_FAIL |}
  | {|
      type: typeof FILTER_PAYMENT_METHODS,
      excludedPaymentMethods: Array<any>
    |}
  | {| type: typeof HOSTED_FIELDS_ERRORS, errors: any |}
  | {| type: typeof SET_GOOGLE_PAY_AVAILABLE |}
  | {| type: typeof SET_APPLE_PAY_AVAILABLE |}
  | {| type: typeof SET_GOOGLE_PAY_HAS_INITIALISED |}
  | {| type: typeof SET_APPLE_PAY_HAS_INITIALISED |}
  | {| type: typeof HOSTED_FIELDS_ERRORS, errors: any |}
  | {| type: typeof CLEAR_GLOBAL_API_ERROR |}
  | {| type: typeof SET_INITIAL_STATE |};

export type PaymentMethod = {|
  id: number,
  source: string,
  authority: ?string,
  isPrimary: boolean,
  isBlocked: boolean,
  isAuthorised: boolean,
  createdAt: string,
  name: string,
  cardType: string,
  lastFour: ?string,
  expiryDate: string,
  email?: string,
  isActive: boolean,
  evCard: boolean
|};

export type State = {|
  loadingPaymentMethods: boolean,
  loadPaymentMethodsComplete: boolean,
  paymentMethods: PaymentMethod[],
  deletingPaymentMethod: boolean,
  displayAllPaymentMethods: boolean,
  userAddingPaymentMethod: boolean,
  newPaymentMethodAdded: boolean,
  paymentDetailsLocked: boolean,
  paymentDetailsSubmitting: boolean,
  hostedFieldErrors: {
    'credit-card-number'?: string,
    expiration?: string,
    cvv?: string,
    message?: string
  },
  apiErrors: {
    availability: ?string
  },
  globalApiError: ?{
    title: string,
    code?: number
  },
  isGooglePayAvailable: boolean,
  isGooglePayActive: boolean,
  hasGooglePayInitialised: boolean,
  isApplePayAvailable: boolean,
  isApplePayActive: boolean,
  hasApplePayInitialised: boolean,
  ddc: {
    jwt: string,
    bin: string,
    errorCode: number,
    errorMessage: string
  }
|};

export const initialState: State = {
  apiErrors: {
    availability: null
  },
  loadPaymentMethodsComplete: false,
  loadingPaymentMethods: false,
  paymentMethods: [],
  hostedFieldErrors: {},
  newPaymentMethodAdded: false,
  paymentDetailsSubmitting: false,
  paymentDetailsLocked: false,
  deletingPaymentMethod: false,
  displayAllPaymentMethods: false,
  globalApiError: null,
  userAddingPaymentMethod: false, // assumed false as we don't want to trigger network requests for the iframes unless required,
  isGooglePayAvailable: false,
  isGooglePayActive: false,
  isApplePayAvailable: false,
  isApplePayActive: false,
  hasGooglePayInitialised: false,
  hasApplePayInitialised: false,
  ddc: {
    jwt: '',
    bin: '',
    errorCode: 0,
    errorMessage: ''
  }
};

export const isMultiUseActive = (paymentMethods) =>
  !!paymentMethods?.find((el) => el.isActive && el.cardType === 'Multi-use');

const booleanKeySort = (key) => (a, b) => {
  if (a[key]) {
    return -1;
  }
  if (b[key]) {
    return 1;
  }
  return 0;
};
const isActiveSort = booleanKeySort('isActive');
const isPrimarySort = booleanKeySort('isPrimary');
const isGooglePaySort = booleanKeySort('isGooglePay');
const isApplePaySort = booleanKeySort('isApplePay');

const sortPaymentMethods = (paymentMethods) =>
  paymentMethods
    .sort(isPrimarySort)
    .sort(isGooglePaySort)
    .sort(isApplePaySort)
    .sort(isActiveSort);

const getPrimaryPaymentMethodId = (methods: PaymentMethod[]): ?number => {
  const results = methods.sort(isPrimarySort);

  return results.length ? results[0].id : null;
};

const noActivePaymentMethod = (paymentMethods) =>
  !isEmpty(paymentMethods) &&
  paymentMethods.filter((item) => item.isActive).length === 0;

const setActivePaymentMethod = (paymentMethods) => {
  const withActivePaymentMethod = [...paymentMethods];
  withActivePaymentMethod[0].isActive = true;

  return withActivePaymentMethod;
};

const isPreferentialMethod = (source: string, cardType: string) =>
  source === 'multi_usage' ||
  source === 'resident_ticket' ||
  cardType === 'Corporate';

const includesPreferentialPaymentMethod = (paymentMethods) =>
  Boolean(
    paymentMethods.filter((item) =>
      isPreferentialMethod(item.source, item.cardType)
    ).length
  );

const includesGooglePayMethod = (paymentMethods) =>
  Boolean(paymentMethods.filter((item) => item.id === 'googlePay').length);
const includesApplePayMethod = (paymentMethods) =>
  Boolean(paymentMethods.filter((item) => item.id === 'applePay').length);
const findActivePaymentMethod = (paymentMethods) =>
  paymentMethods.filter((paymentMethod) => paymentMethod.isActive)[0];

const isGoogleOrApplePaymentMethod = (
  id: string,
  hasGooglePayMethod: boolean,
  hasApplePayMethod: boolean
) =>
  (hasApplePayMethod && id === 'applePay') ||
  (hasGooglePayMethod && id === 'googlePay');

/*
Returns if a payment method isActive

1. If it is a preferential method
2. If there are no preferential methods, check if there are ApplePay / GooglePay
3. Finally the primary payment method

The payment methods are also sorted in this order.
 */

const isNewPaymentMethodActive = (
  id: string,
  primaryPaymentId: string,
  source: string,
  cardType: string,
  hasPreferentialPaymentMethod: boolean,
  isOnCardTab: boolean,
  hasGooglePayMethod: boolean,
  hasApplePayMethod: boolean
) => {
  if (isOnCardTab && isPreferentialMethod(source, cardType)) {
    return true;
  }

  if (
    isOnCardTab &&
    !hasPreferentialPaymentMethod &&
    isGoogleOrApplePaymentMethod(id, hasGooglePayMethod, hasApplePayMethod)
  ) {
    return true;
  }

  if (
    (isOnCardTab &&
      !hasPreferentialPaymentMethod &&
      !hasGooglePayMethod &&
      !hasApplePayMethod) ||
    !isOnCardTab
  ) {
    return id === primaryPaymentId;
  }

  return false;
};

const filterNewPaymentMethods = (
  paymentMethods: Array<PaymentMethod>,
  primaryPaymentId: string | number | null,
  isOnCardTab: boolean
) => {
  if (!paymentMethods.length) {
    return paymentMethods;
  }

  const hasPreferentialPaymentMethod = includesPreferentialPaymentMethod(
    paymentMethods
  );
  const hasGooglePayMethod = includesGooglePayMethod(paymentMethods);
  const hasApplePayMethod = includesApplePayMethod(paymentMethods);

  let hasAnActivePaymentMethod;
  return paymentMethods
    .filter((item) => item.cardType && item.cardType.toLowerCase())
    .sort(isPrimarySort)
    .sort(isApplePaySort)
    .map((paymentMethod) => {
      const { id, source, cardType } = paymentMethod;

      const isActive = isNewPaymentMethodActive(
        id,
        primaryPaymentId,
        source,
        cardType,
        hasPreferentialPaymentMethod,
        isOnCardTab,
        hasGooglePayMethod,
        hasApplePayMethod
      );

      if (isActive && !hasAnActivePaymentMethod) {
        hasAnActivePaymentMethod = true;

        return { ...paymentMethod, isActive };
      }

      return {
        ...paymentMethod,
        isActive: false
      };
    });
};

export const ddcLookup = createAsyncThunk(
  'payments/ddcLookup',
  async (paymentMethodId, thunkApi) => {
    const {
      extra: { client },
      rejectWithValue
    } = thunkApi;

    try {
      // TODO: Handle virtual payment methods and multiuse
      return await client.paymentMethods.ddc(paymentMethodId);
    } catch (e) {
      // TODO: Error handling
      return rejectWithValue(e);
    }
  }
);

export const jwtLookup = createAsyncThunk(
  'payments/jwtLookup',
  async (payload, thunkApi) => {
    const {
      extra: { client },
      rejectWithValue
    } = thunkApi;

    try {
      return await client.paymentMethods.jwt();
    } catch (e) {
      return rejectWithValue(e);
    }
  }
);

export const loadPaymentMethods = createAsyncThunk(
  'payments/loadPaymentMethods',
  async (payload, thunkApi) => {
    const {
      rejectWithValue,
      extra: { jpApiClient }
    } = thunkApi;

    try {
      const request = getPaymentMethods(payload);
      const response = await jpApiClient(request);

      return response.data;
    } catch (e) {
      return rejectWithValue(getRequestErrorData(e));
    }
  }
);

export const createPaymentMethod = createAsyncThunk(
  'payments/createPaymentMethod',
  async (payload, thunkApi) => {
    const {
      extra: { jpApiClient },
      rejectWithValue
    } = thunkApi;

    try {
      const {
        nonce,
        model: { postalCode, cardholderName, country }
      } = payload;

      const request = createPaymentMethodApi(
        nonce,
        cardholderName,
        country,
        postalCode
      );
      const response = await jpApiClient(request);

      return response.data;
    } catch (e) {
      return rejectWithValue(getRequestErrorData(e));
    }
  }
);

export const deletePaymentMethod = createAsyncThunk(
  'payments/deletePaymentMethod',
  async (payload, thunkApi) => {
    const {
      extra: { jpApiClient },
      rejectWithValue
    } = thunkApi;

    try {
      const request = deletePaymentMethodApi(payload);
      await jpApiClient(request);

      return { id: payload };
    } catch (e) {
      return rejectWithValue(getRequestErrorData(e));
    }
  }
);

const paymentsSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    resetDdc: (state) => ({
      ...state,
      ddc: {
        ...initialState.ddc
      }
    }),
    setActiveEVPaymentMethod: (state) => {
      const hasAnActiveEVPaymentMethod = state.paymentMethods.find(
        (item) => item.evCard
      );
      if (hasAnActiveEVPaymentMethod) {
        return {
          ...state,
          paymentMethods: state.paymentMethods.map((item) =>
            item.evCard
              ? { ...item, isActive: true }
              : { ...item, isActive: false }
          )
        };
      }
      return state;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(SET_INITIAL_STATE, (): State => {
      debug(SET_INITIAL_STATE);
      return {
        ...initialState
      };
    });
    builder.addCase(loadPaymentMethods.pending, (state: State): State => {
      debug('loadPaymentMethods.pending');

      return { ...state, loadingPaymentMethods: true };
    });
    builder.addCase(
      loadPaymentMethods.fulfilled,
      (state: State, action: Action): State => {
        debug('loadPaymentMethods.fulfilled', action);

        if (action.payload && action.payload.data) {
          const rawPaymentMethods = action.payload.data;
          const allPaymentMethods = [
            ...rawPaymentMethods,
            ...state.paymentMethods
          ];

          const paymentMethods = filterNewPaymentMethods(
            allPaymentMethods,
            rawPaymentMethods[0]?.id,
            true
          );

          const sortedPaymentMethods = sortPaymentMethods(paymentMethods);
          const newActivePaymentMethod = findActivePaymentMethod(
            sortedPaymentMethods
          );

          return {
            ...state,
            loadingPaymentMethods: false,
            loadPaymentMethodsComplete: true,
            paymentMethods: noActivePaymentMethod(sortedPaymentMethods)
              ? [...setActivePaymentMethod(sortedPaymentMethods)]
              : [...sortedPaymentMethods],
            paymentDetailsLocked: !isEmpty(paymentMethods),
            userAddingPaymentMethod: sortedPaymentMethods.length <= 0,
            isGooglePayActive:
              newActivePaymentMethod &&
              newActivePaymentMethod.id === 'googlePay',
            isApplePayActive:
              newActivePaymentMethod && newActivePaymentMethod.id === 'applePay'
          };
        }

        // Notify error to bugsnag
        notify(
          new Error('No payment methods in checkout get payment methods'),
          {
            metaData: action
          }
        );

        return {
          ...state,
          loadingPaymentMethods: false,
          loadPaymentMethodsComplete: true,
          paymentMethods: [],
          paymentDetailsLocked: false,
          userAddingPaymentMethod: true
        };
      }
    );
    builder.addCase(
      loadPaymentMethods.rejected,
      (state: State, action: Action): State => {
        debug('loadPaymentMethods.rejected', action);

        return {
          ...state,
          loadingPaymentMethods: false,
          loadPaymentMethodsComplete: false
        };
      }
    );
    builder.addCase(
      FILTER_PAYMENT_METHODS,
      (state: State, action: Action): State => {
        debug(FILTER_PAYMENT_METHODS, action);
        if (
          'excludedPaymentMethods' in action &&
          action.excludedPaymentMethods.length
        ) {
          const paymentMethods = JSON.parse(
            JSON.stringify(state.paymentMethods)
          );

          const excludedLower = action.excludedPaymentMethods.map((el) =>
            el.toLowerCase()
          );

          const filteredPaymentMethods = paymentMethods.filter((el) => {
            const allowedByType = !excludedLower.includes(
              el.cardType && el.cardType.toLowerCase()
            );
            const allowedBySource = !excludedLower.includes(
              el.source && el.source.toLowerCase()
            );

            return allowedByType && allowedBySource;
          });
          const newActivePaymentMethod = filteredPaymentMethods.filter(
            (paymentMethod) => paymentMethod.isActive
          )[0];

          const excludesGoogle = !!excludedLower.filter(
            (el) => el === 'google pay' || el === 'google_pay'
          ).length;
          const excludesApple = !!excludedLower.filter(
            (el) => el === 'apple pay' || el === 'apple_pay'
          ).length;

          // Create a new instance of payment item object if the type of payment
          // is available
          const applePayItem =
            !excludesApple && state.isApplePayAvailable
              ? [{ ...applePaymentItem }]
              : [];
          const googlePayItem =
            !excludesGoogle && state.isGooglePayAvailable
              ? [{ ...googlePaymentItem }]
              : [];

          // Checks to see if the array of payment methods already has the
          // apple pay/google pay item and adds it if it is available
          let combinedPaymentMethods = filteredPaymentMethods.find(
            (item) => item.source === 'apple_pay'
          )
            ? filteredPaymentMethods
            : [...filteredPaymentMethods, ...applePayItem];

          combinedPaymentMethods = combinedPaymentMethods.find(
            (item) =>
              item.source === 'google_pay' || item.source === 'apple_pay'
          )
            ? combinedPaymentMethods
            : [...combinedPaymentMethods, ...googlePayItem];

          const newPaymentMethods = noActivePaymentMethod(
            combinedPaymentMethods
          )
            ? [...setActivePaymentMethod(combinedPaymentMethods)]
            : [...combinedPaymentMethods];

          return {
            ...state,
            paymentDetailsLocked:
              newPaymentMethods.length === 0
                ? false
                : state.paymentDetailsLocked,
            paymentMethods: newPaymentMethods,
            userAddingPaymentMethod: combinedPaymentMethods.length <= 0,
            isGooglePayActive:
              newActivePaymentMethod &&
              newActivePaymentMethod.id === 'googlePay',
            isApplePayActive:
              newActivePaymentMethod && newActivePaymentMethod.id === 'applePay'
          };
        }
        return { ...state };
      }
    );
    builder.addCase(
      SELECT_PAYMENTMETHOD,
      (state: State, action: Action): State => {
        debug(SELECT_PAYMENTMETHOD, action);
        if (action && action.id) {
          const { id } = action;
          const { paymentMethods } = state;
          const newPaymentMethods = [
            ...paymentMethods.map((paymentMethod) => ({
              ...paymentMethod,
              isActive: paymentMethod.id === id
            }))
          ];

          return {
            ...state,
            paymentMethods: newPaymentMethods,
            isGooglePayActive: id === 'googlePay',
            isApplePayActive: id === 'applePay',
            ddcErrorCode: '',
            ddcErrorMessage: ''
          };
        }
        return state;
      }
    );
    builder.addCase(deletePaymentMethod.pending, (state: State): State => {
      debug('deletePaymentMethod.pending');

      return { ...state, deletingPaymentMethod: true };
    });
    builder.addCase(
      deletePaymentMethod.fulfilled,
      (state: State, action: Action): State => {
        debug('deletePaymentMethod.fulfilled', action);

        if (action.payload?.id) {
          const { paymentMethods } = state;
          let newPaymentMethods = [...paymentMethods];
          const idx = getIdxById(paymentMethods, action.payload.id);

          newPaymentMethods.splice(idx, 1);

          // If the item we deleted was active set a new active method item
          if (paymentMethods[idx].isActive && newPaymentMethods.length >= 1) {
            const newActiveId = getPrimaryPaymentMethodId(newPaymentMethods);

            newPaymentMethods = newPaymentMethods.map((method) => ({
              ...method,
              isActive: method.id === newActiveId
            }));
          }

          const newActivePaymentMethod = newPaymentMethods.filter(
            (paymentMethod) => paymentMethod.isActive
          )[0];

          return {
            ...state,
            paymentMethods: sortPaymentMethods(newPaymentMethods),
            deletingPaymentMethod: false,
            userAddingPaymentMethod: newPaymentMethods.length === 0,
            isGooglePayActive:
              newActivePaymentMethod &&
              newActivePaymentMethod.id === 'googlePay',
            isApplePayActive:
              newActivePaymentMethod && newActivePaymentMethod.id === 'applePay'
          };
        }

        notify(new Error('No payment method id in delete action'));

        return {
          ...state,
          globalApiError: {
            title: 'Something went wrong trying to delete your payment source'
          },
          deletingPaymentMethod: false
        };
      }
    );
    builder.addCase(
      deletePaymentMethod.rejected,
      (state: State, action: Action): State => {
        debug('deletePaymentMethod.rejected', action);

        if (action.payload && action.payload.data) {
          return {
            ...state,
            globalApiError: {
              title: action.payload.data.title
            },
            deletingPaymentMethod: false
          };
        }

        return {
          ...state,
          globalApiError: {
            title: 'Something went wrong trying to delete your payment source'
          },
          deletingPaymentMethod: false
        };
      }
    );
    builder.addCase(TOGGLE_ALL_PAYMENTMETHODS, (state: State): State => {
      debug(TOGGLE_ALL_PAYMENTMETHODS);
      return {
        ...state,
        displayAllPaymentMethods: !state.displayAllPaymentMethods
      };
    });
    builder.addCase(
      TOGGLE_USER_ADD_PAYMENTMETHOD,
      (state: State, action: Action): State => {
        debug(TOGGLE_USER_ADD_PAYMENTMETHOD, action);
        if (action && 'bool' in action) {
          const primaryMethodId = getPrimaryPaymentMethodId([
            ...state.paymentMethods
          ]);

          // If user is adding a payment method mark all other methods as not active
          // Otherwise mark the first primary method as active
          const paymentMethods = action.bool
            ? [...state.paymentMethods].map((method) => ({
                ...method,
                isActive: false
              }))
            : [...state.paymentMethods].map((method) => ({
                ...method,
                isActive: primaryMethodId === method.id
              }));

          return {
            ...state,
            userAddingPaymentMethod: action.bool,
            paymentMethods
          };
        }

        return {
          ...state
        };
      }
    );
    builder.addCase(
      SET_PAYMENT_DETAILS_SUBMITTING,
      (state: State, action: Action): State => {
        debug(SET_PAYMENT_DETAILS_SUBMITTING);

        if (typeof action.submitting === 'boolean') {
          return {
            ...state,
            paymentDetailsSubmitting: action.submitting
          };
        }

        return {
          ...state
        };
      }
    );
    builder.addCase(
      SET_PAYMENT_DETAILS_LOCK,
      (state: State, action: Action): State => {
        debug(SET_PAYMENT_DETAILS_LOCK);

        if (typeof action.lock === 'boolean') {
          return {
            ...state,
            paymentDetailsLocked: action.lock,
            paymentMethods: sortPaymentMethods([...state.paymentMethods])
          };
        }

        return {
          ...state
        };
      }
    );
    builder.addCase(
      HOSTED_FIELDS_ERRORS,
      (state: State, action: Action): State => {
        debug(HOSTED_FIELDS_ERRORS, action);
        if (action && action.errors) {
          return {
            ...state,
            hostedFieldErrors: action.errors
          };
        }

        notify(new Error('No errors in hosted fields errors action'), {
          metaData: action
        });

        return {
          ...state,
          globalApiError: {
            title: 'There has been an error'
          }
        };
      }
    );
    builder.addCase(CARD_TOKENIZING, (state: State): State => {
      debug(CARD_TOKENIZING);

      // When adding a new card they payment source is added, payment details locked and the primary submit and pay button then becomes active
      // However becuase the payment method ID has changed we call off to get a new booking summary, this causes the pay button to become disabled again
      // This causes a weird looking quick transition from enabled -> disabled -> enabled we want to get rid of.
      // newPaymentMethodAdded is stored here so we can keep that button disabled until the summary endpoint clears it

      return {
        ...state,
        newPaymentMethodAdded: true
      };
    });
    builder.addCase(createPaymentMethod.pending, (state: State): State => {
      debug('createPaymentMethod.pending');
      // clear our any apiError we previously received from the api trying to create a payment method
      const apiErrors = omit(state.apiErrors, ['paymentMethodId']);

      return { ...state, paymentDetailsSubmitting: true, apiErrors };
    });
    builder.addCase(
      createPaymentMethod.fulfilled,
      (state: State, action: Action): State => {
        debug('createPaymentMethod.fulfilled', action);

        if (action.payload && action.payload.data) {
          const apiErrors = omit(state.apiErrors, ['paymentMethodId']);

          const newItem = action.payload.data;

          const newPaymentMethods = [newItem, ...state.paymentMethods].map(
            (paymentMethod) => ({
              ...paymentMethod,
              // Set the new item as active if we are showing the tab this method belongs in
              isActive: paymentMethod.id === newItem.id
            })
          );

          return {
            ...state,
            apiErrors,
            paymentMethods: newPaymentMethods,
            paymentDetailsLocked: true,
            userAddingPaymentMethod: false,
            paymentDetailsSubmitting: false,
            hostedFieldErrors: {},
            isGooglePayActive: false,
            isApplePayActive: false
          };
        }

        notify(new Error('No payment method in response'), {
          metaData: action
        });

        return {
          ...state,
          paymentDetailsSubmitting: false
        };
      }
    );
    builder.addCase(
      createPaymentMethod.rejected,
      (state: State, action: Action): State => {
        debug('createPaymentMethod.rejected', action);

        if (action.payload && action.payload.data) {
          return {
            ...state,
            globalApiError: action.payload.data,
            paymentDetailsSubmitting: false
          };
        }

        return {
          ...state,
          globalApiError: {
            title: 'Error creating payment method'
          },
          paymentDetailsSubmitting: false
        };
      }
    );
    builder.addCase(CLEAR_GLOBAL_API_ERROR, (state: State): State => {
      debug(CLEAR_GLOBAL_API_ERROR);
      return {
        ...state,
        globalApiError: null
      };
    });
    builder.addCase(SET_GOOGLE_PAY_AVAILABLE, (state: State) => {
      debug(SET_GOOGLE_PAY_AVAILABLE);

      if (state.isGooglePayAvailable || state.isApplePayActive) {
        return { ...state, hasGooglePayInitialised: true };
      }

      const paymentMethods = [...state.paymentMethods];

      const primaryPaymentMethodId = getPrimaryPaymentMethodId(paymentMethods);
      const newPaymentMethods = filterNewPaymentMethods(
        [...paymentMethods, googlePaymentItem],
        primaryPaymentMethodId,
        true
      );

      const newActivePaymentMethod = findActivePaymentMethod(newPaymentMethods);

      return {
        ...state,
        paymentDetailsLocked:
          state.loadPaymentMethodsComplete && state.hasApplePayInitialised
            ? true
            : state.paymentDetailsLocked,
        isGooglePayAvailable: true,
        isGooglePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'googlePay',
        isApplePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'applePay',
        hasGooglePayInitialised: true,
        paymentMethods: sortPaymentMethods(newPaymentMethods),
        userAddingPaymentMethod: false
      };
    });
    builder.addCase(SET_GOOGLE_PAY_HAS_INITIALISED, (state: State) => {
      debug(SET_GOOGLE_PAY_HAS_INITIALISED);

      return {
        ...state,
        hasGooglePayInitialised: true
      };
    });
    builder.addCase(SET_APPLE_PAY_HAS_INITIALISED, (state: State) => {
      debug(SET_APPLE_PAY_HAS_INITIALISED);

      return {
        ...state,
        hasApplePayInitialised: true
      };
    });
    builder.addCase(SET_APPLE_PAY_AVAILABLE, (state: State) => {
      debug(SET_APPLE_PAY_AVAILABLE);

      if (state.isApplePayAvailable) {
        return { ...state, hasApplePayInitialised: true };
      }

      const paymentMethods = [...state.paymentMethods];

      const primaryPaymentMethodId = getPrimaryPaymentMethodId(paymentMethods);

      const newPaymentMethods = filterNewPaymentMethods(
        [...paymentMethods, applePaymentItem],
        primaryPaymentMethodId,
        true
      );

      const newActivePaymentMethod = findActivePaymentMethod(newPaymentMethods);

      return {
        ...state,
        paymentDetailsLocked:
          state.loadPaymentMethodsComplete && state.hasGooglePayInitialised
            ? true
            : state.paymentDetailsLocked,
        isApplePayAvailable: true,
        isApplePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'applePay',
        hasApplePayInitialised: true,
        isGooglePayActive:
          newActivePaymentMethod && newActivePaymentMethod.id === 'googlePay',
        paymentMethods: sortPaymentMethods(newPaymentMethods),
        userAddingPaymentMethod: false
      };
    });
    builder.addCase(ddcLookup.pending, (state: State) => {
      debug('ddcLookup.pending');

      return {
        ...state,
        ddc: {
          jwt: '',
          bin: '',
          errorCode: 0,
          errorMessage: ''
        }
      };
    });
    builder.addCase(ddcLookup.fulfilled, (state: State, action) => {
      debug('ddcLookup.fulfilled', action);
      const { jwt, bin } = action.payload;

      return {
        ...state,
        ddc: {
          jwt,
          bin,
          errorCode: 0,
          errorMessage: ''
        }
      };
    });
    builder.addCase(ddcLookup.rejected, (state: State, action) => {
      debug('ddcLookup.rejected', action);

      return {
        ...state,
        ddc: {
          jwt: '',
          bin: '',
          errorCode: action.payload.error,
          errorMessage: action.payload.message
        }
      };
    });

    builder.addCase(jwtLookup.pending, (state: State) => {
      debug('jwtLookup.pending');

      return {
        ...state,
        ddc: {
          jwt: '',
          bin: '',
          errorCode: 0,
          errorMessage: ''
        }
      };
    });
    builder.addCase(jwtLookup.fulfilled, (state: State, action) => {
      debug('jwtLookup.fulfilled', action);
      const { jwt } = action.payload;

      return {
        ...state,
        ddc: {
          jwt,
          bin: '',
          errorCode: 0,
          errorMessage: ''
        }
      };
    });
    builder.addCase(jwtLookup.rejected, (state: State, action) => {
      debug('jwtLookup.rejected', action);

      return {
        ...state,
        ddc: {
          jwt: '',
          bin: '',
          errorCode: action.payload.error,
          errorMessage: action.payload.message
        }
      };
    });
  }
});

export default paymentsSlice;

export const { resetDdc, setActiveEVPaymentMethod } = paymentsSlice.actions;

const selectPaymentsDdcJwt = (state) => state[sliceName].ddc.jwt;
const selectPaymentsDdcBin = (state) => state[sliceName].ddc.bin;
const selectPaymentsDdcErrorCode = (state) => state[sliceName].ddc.errorCode;
const selectPaymentsDdcErrorMessage = (state) =>
  state[sliceName].ddc.errorMessage;

export const selectPaymentsDdcData = createSelector(
  selectPaymentsDdcJwt,
  selectPaymentsDdcBin,
  selectPaymentsDdcErrorCode,
  selectPaymentsDdcErrorMessage,
  (jwt, bin, errorCode, errorMessage) => ({
    jwt,
    bin,
    errorCode,
    errorMessage
  })
);

export const selectPaymentMethod = makeActionCreator(
  SELECT_PAYMENTMETHOD,
  'id'
);

export const toggleUserAddPaymentMethod = makeActionCreator(
  TOGGLE_USER_ADD_PAYMENTMETHOD,
  'bool'
);

export const setPaymentDetailsLock = makeActionCreator(
  SET_PAYMENT_DETAILS_LOCK,
  'lock'
);

export const setPaymentDetailsSubmitting = makeActionCreator(
  SET_PAYMENT_DETAILS_SUBMITTING,
  'submitting'
);

export const handleHostedFieldsErrors = makeActionCreator(
  HOSTED_FIELDS_ERRORS,
  'errors'
);

export const setCardTokenizing = makeActionCreator(CARD_TOKENIZING);

export const toggleAllPaymentMethods = makeActionCreator(
  TOGGLE_ALL_PAYMENTMETHODS
);

export const filterPaymentMethods = makeActionCreator(
  FILTER_PAYMENT_METHODS,
  'excludedPaymentMethods'
);

export const clearGlobalApiError = makeActionCreator(CLEAR_GLOBAL_API_ERROR);

export const setGooglePayAvailable = makeActionCreator(
  SET_GOOGLE_PAY_AVAILABLE
);

export const setGooglePayHasInitialised = makeActionCreator(
  SET_GOOGLE_PAY_HAS_INITIALISED
);

export const setApplePayAvailable = makeActionCreator(SET_APPLE_PAY_AVAILABLE);

export const setApplePayHasInitialised = makeActionCreator(
  SET_APPLE_PAY_HAS_INITIALISED
);

export const setInitialState = makeActionCreator(SET_INITIAL_STATE);

export const selectPaymentMethods = (state) => state[sliceName].paymentMethods;
export const selectActivePaymentMethod = createSelector(
  selectPaymentMethods,
  (paymentMethods) =>
    paymentMethods && paymentMethods.find((item) => item.isActive)
);
export const selectActivePaymentMethodId = createSelector(
  selectActivePaymentMethod,
  (activePaymentMethod) => activePaymentMethod?.id
);

export const selectLoadingPaymentMethods = (state) =>
  state[sliceName].loadingPaymentMethods;
export const selectPaymentDetailsLocked = (state) =>
  state[sliceName].paymentDetailsLocked;
export const selectPaymentUserAddingPaymentMethod = (state) =>
  state[sliceName].userAddingPaymentMethod;

export const selectPaymentHasApplePayInitialised = (state) =>
  state[sliceName].hasApplePayInitialised;

export const selectPaymentHasGooglePayInitialised = (state) =>
  state[sliceName].hasGooglePayInitialised;

export const selectLoadPaymentMethodsComplete = (state) =>
  state[sliceName].loadPaymentMethodsComplete;
export const selectIsGooglePayActive = (state) =>
  state[sliceName].isGooglePayActive;
export const selectIsApplePayActive = (state) =>
  state[sliceName].isApplePayActive;
export const selectNewPaymentMethodAdded = (state) =>
  state[sliceName].newPaymentMethodAdded;
